import { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiEye, FiMoreVertical, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import API from "../api/axios";
import TablePagination from "../components/TablePagination";
import TableMenuPopover from "../components/TableMenuPopover";
import formatImageUrl from "../utils/imageUrl";

const initialForm = {
  name: "",
  type: "tour",
  category: "spiritual",
  destination: "",
  durationDays: "",
  durationNights: "",
  price: "",
  priceUnit: "per_person",
  billingCycle: "one_time",
  toursCount: "",
  validityYears: "",
  zonesCount: "",
  travelDates: "",
  description: "",
  highlights: "",
  inclusions: "",
  itinerary: [{ day: 1, title: "", description: "" }],
  isActive: true,
  isFeatured: false,
};

const typeLabels = {
  tour: "Tour Package",
  subscription: "Subscription",
};

const categoryLabels = {
  spiritual: "Spiritual",
  domestic: "Domestic",
  international: "International",
  honeymoon: "Honeymoon",
  group: "Group",
  membership: "Membership",
  cultural: "Cultural",
};

const priceUnitLabels = {
  per_person: "Per Person",
  per_couple: "Per Couple",
  per_year: "Per Year",
  on_request: "On Request",
};

const formatPrice = (item) => {
  if (item?.priceUnit === "on_request" || !Number(item?.price)) {
    return "On Request";
  }
  return `₹${Number(item.price).toLocaleString("en-IN")} ${priceUnitLabels[item.priceUnit] || ""}`.trim();
};

const formatDuration = (item) => {
  const days = Number(item?.durationDays || 0);
  const nights = Number(item?.durationNights || 0);
  if (!days && !nights) return "-";
  if (days && nights) return `${days}D / ${nights}N`;
  if (days) return `${days} Days`;
  return `${nights} Nights`;
};

const toList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

function PackageViewPreview({ item, onClose }) {
  if (!item) return null;

  const isMembership = item.type === "subscription";
  const highlights = toList(item.highlights);
  const inclusions = toList(item.inclusions);
  const itinerary = toList(item.itinerary);

  return (
    <section className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7b5a4b]">
            Customer view preview
          </p>
          <h3 className="text-xl font-bold">
            {isMembership ? "How the membership will show" : "How the tour package will show"}
          </h3>
        </div>
        <button type="button" onClick={onClose} className="p-1" title="Close preview">
          <FiX size={22} />
        </button>
      </div>

      <div className="overflow-hidden rounded-[28px] border border-[#d7c3a3] bg-[#fffaf1] text-[#102C57] shadow-lg dark:border-white/10 dark:bg-[#141820] dark:text-[#fff3dc]">
        <div className="relative min-h-[220px] bg-[linear-gradient(135deg,#102C57,#1b3f7a)] p-8 text-white">
          {item.image ? (
            <img
              src={formatImageUrl(item.image)}
              alt={item.name}
              className="absolute inset-0 h-full w-full object-cover opacity-30"
            />
          ) : null}
          <div className="relative max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E7C98B]">
              {isMembership ? "Yatra369 Membership" : "Yatra369 Tour Package"}
            </p>
            <h2 className="mt-3 font-serif text-4xl font-bold">{item.name}</h2>
            <p className="mt-3 max-w-xl text-sm text-white/85">
              {item.description ||
                (isMembership
                  ? "Pay less, utilize more, travel more. A 5-year spiritual travel membership for 2 members."
                  : "A thoughtfully planned temple and cultural journey.")}
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold">
              {item.destination ? (
                <span className="rounded-full bg-white/15 px-3 py-1">{item.destination}</span>
              ) : null}
              <span className="rounded-full bg-white/15 px-3 py-1">{categoryLabels[item.category] || item.category}</span>
              {isMembership ? (
                <span className="rounded-full bg-[#C9A227] px-3 py-1 text-[#102C57]">
                  {item.toursCount || 10} tours · {item.validityYears || 5} years
                </span>
              ) : (
                <span className="rounded-full bg-[#C9A227] px-3 py-1 text-[#102C57]">{formatDuration(item)}</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-6 p-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-5">
            {isMembership ? (
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  { label: "Tours", value: `${item.toursCount || 10}` },
                  { label: "Validity", value: `${item.validityYears || 5} Years` },
                  { label: "Zones", value: `${item.zonesCount || 4}` },
                ].map((card) => (
                  <div key={card.label} className="rounded-2xl border border-[#ecd9b6] bg-white p-4 dark:border-white/10 dark:bg-[#1b1f27]">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#7b5a4b]">{card.label}</p>
                    <p className="mt-1 text-2xl font-bold text-[#102C57] dark:text-[#E7C98B]">{card.value}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {highlights.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#7b5a4b]">Highlights</h4>
                <div className="flex flex-wrap gap-2">
                  {highlights.map((line) => (
                    <span key={line} className="rounded-full bg-[#102C57]/8 px-3 py-1 text-sm">
                      {line}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {inclusions.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#7b5a4b]">Inclusions</h4>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {inclusions.map((line) => (
                    <li key={line} className="rounded-xl border border-[#ecd9b6] px-3 py-2 text-sm dark:border-white/10">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!isMembership && itinerary.length > 0 && (
              <div>
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#7b5a4b]">Day-wise itinerary</h4>
                <div className="space-y-3">
                  {itinerary.map((row, index) => (
                    <div key={`${row.day}-${index}`} className="rounded-2xl border border-[#ecd9b6] p-4 dark:border-white/10">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C9A227]">Day {row.day || index + 1}</p>
                      <p className="mt-1 font-semibold">{row.title || "Journey day"}</p>
                      {row.description ? <p className="mt-1 text-sm text-[#5c4033] dark:text-[#dbcdb8]">{row.description}</p> : null}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isMembership && (
              <div>
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#7b5a4b]">How membership works</h4>
                <ol className="grid gap-2 sm:grid-cols-2">
                  {[
                    "Apply for 2 members",
                    "Yatra369 reviews eligibility",
                    "Pay ₹60,000 membership fee",
                    "Get Membership ID",
                    "Membership becomes active",
                    "Select an eligible tour",
                    "Confirm travel dates",
                    "Travel with the second member",
                    "Each tour counts in 10",
                    "Repeat within 5 years",
                  ].map((step, index) => (
                    <li key={step} className="flex gap-3 rounded-xl bg-white px-3 py-2 text-sm dark:bg-[#1b1f27]">
                      <span className="font-bold text-[#C9A227]">{String(index + 1).padStart(2, "0")}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <aside className="h-fit rounded-[24px] border border-[#ecd9b6] bg-white p-5 dark:border-white/10 dark:bg-[#1b1f27]">
            <p className="text-xs uppercase tracking-[0.2em] text-[#7b5a4b]">
              {isMembership ? "Membership fee" : "Package price"}
            </p>
            <p className="mt-2 font-serif text-4xl font-bold text-[#102C57] dark:text-[#E7C98B]">{formatPrice(item)}</p>
            {item.travelDates ? (
              <p className="mt-3 text-sm text-[#5c4033] dark:text-[#dbcdb8]">Dates: {item.travelDates}</p>
            ) : null}
            <button type="button" className="mt-5 w-full rounded-2xl bg-[#102C57] px-4 py-3 text-sm font-semibold text-white">
              {isMembership ? "Apply for Membership" : "Enquire / Book Tour"}
            </button>
            <p className="mt-3 text-center text-[11px] text-[#7b5a4b]">
              Preview only. App user side later me enable hoga.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openMenuId, setOpenMenuId] = useState("");
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");

  const filteredPackages = useMemo(() => {
    return packages.filter((item) => {
      if (activeTab !== "all" && item.type !== activeTab) return false;
      if (!query.trim()) return true;
      const haystack = `${item.name} ${item.destination} ${item.category}`.toLowerCase();
      return haystack.includes(query.trim().toLowerCase());
    });
  }, [packages, activeTab, query]);

  const pagedPackages = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPackages.slice(start, start + pageSize);
  }, [filteredPackages, page, pageSize]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const res = await API.get("/admin/packages");
      setPackages(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load packages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filteredPackages.length, activeTab, query]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!event.target.closest("[data-package-menu], [data-table-menu-popover]")) {
        setOpenMenuId("");
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setEditingId("");
    setImageFile(null);
    setImagePreview("");
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => {
      const next = {
        ...current,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "type" && value === "subscription") {
        next.category = current.category === "spiritual" ? "membership" : current.category;
        next.billingCycle = current.billingCycle === "one_time" ? "yearly" : current.billingCycle;
        next.priceUnit = current.priceUnit === "per_person" ? "per_couple" : current.priceUnit;
      }
      return next;
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : "");
  };

  const updateItinerary = (index, field, value) => {
    setForm((current) => {
      const itinerary = current.itinerary.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: field === "day" ? Number(value || 1) : value } : row
      );
      return { ...current, itinerary };
    });
  };

  const addItineraryDay = () => {
    setForm((current) => ({
      ...current,
      itinerary: [
        ...current.itinerary,
        { day: current.itinerary.length + 1, title: "", description: "" },
      ],
    }));
  };

  const removeItineraryDay = (index) => {
    setForm((current) => ({
      ...current,
      itinerary: current.itinerary.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const openCreate = () => {
    resetForm();
    setError("");
    setSuccess("");
    setShowForm(true);
  };

  const openEdit = (item) => {
    setForm({
      name: item?.name || "",
      type: item?.type || "tour",
      category: item?.category || "spiritual",
      destination: item?.destination || "",
      durationDays: item?.durationDays || "",
      durationNights: item?.durationNights || "",
      price: item?.price || "",
      priceUnit: item?.priceUnit || "per_person",
      billingCycle: item?.billingCycle || "one_time",
      toursCount: item?.toursCount || "",
      validityYears: item?.validityYears || "",
      zonesCount: item?.zonesCount || "",
      travelDates: item?.travelDates || "",
      description: item?.description || "",
      highlights: (item?.highlights || []).join(", "),
      inclusions: (item?.inclusions || []).join(", "),
      itinerary: item?.itinerary?.length
        ? item.itinerary.map((row, index) => ({
            day: row.day || index + 1,
            title: row.title || "",
            description: row.description || "",
          }))
        : [{ day: 1, title: "", description: "" }],
      isActive: item?.isActive !== false,
      isFeatured: Boolean(item?.isFeatured),
    });
    setImageFile(null);
    setImagePreview(item?.image ? formatImageUrl(item.image) : "");
    setEditingId(item?._id || "");
    setError("");
    setSuccess("");
    setShowForm(true);
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError("Package name is required.");
      setSuccess("");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("type", form.type);
      payload.append("category", form.category);
      payload.append("destination", form.destination.trim());
      payload.append("durationDays", form.durationDays || 0);
      payload.append("durationNights", form.durationNights || 0);
      payload.append("price", form.price || 0);
      payload.append("priceUnit", form.priceUnit);
      payload.append("billingCycle", form.billingCycle);
      payload.append("toursCount", form.toursCount || 0);
      payload.append("validityYears", form.validityYears || 0);
      payload.append("zonesCount", form.zonesCount || 0);
      payload.append("travelDates", form.travelDates.trim());
      payload.append("description", form.description.trim());
      payload.append("highlights", form.highlights);
      payload.append("inclusions", form.inclusions);
      payload.append("itinerary", JSON.stringify(form.itinerary));
      payload.append("isActive", String(form.isActive));
      payload.append("isFeatured", String(form.isFeatured));
      if (imageFile) {
        payload.append("imageFile", imageFile);
      }

      if (editingId) {
        await API.put(`/admin/packages/${editingId}`, payload);
      } else {
        await API.post("/admin/packages", payload);
      }

      await fetchPackages();
      setSuccess(editingId ? "Package updated successfully." : "Package created successfully.");
      closeForm();
    } catch (err) {
      setError(err.response?.data?.message || "Unable to save package.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!item?._id) return;
    if (!window.confirm(`Delete package "${item.name}"?`)) return;

    try {
      setDeletingId(item._id);
      setError("");
      setSuccess("");
      await API.delete(`/admin/packages/${item._id}`);
      setPackages((current) => current.filter((entry) => entry._id !== item._id));
      setSuccess("Package deleted successfully.");
      if (editingId === item._id) closeForm();
    } catch (err) {
      setError(err.response?.data?.message || "Unable to delete package.");
    } finally {
      setDeletingId("");
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      const payload = new FormData();
      payload.append("name", item.name);
      payload.append("type", item.type);
      payload.append("isActive", String(!item.isActive));
      await API.put(`/admin/packages/${item._id}`, payload);
      setPackages((current) =>
        current.map((entry) =>
          entry._id === item._id ? { ...entry, isActive: !item.isActive } : entry
        )
      );
    } catch (err) {
      setError(err.response?.data?.message || "Unable to update status.");
    }
  };

  const inputClass = "w-full rounded-xl border border-[var(--admin-border)] bg-[var(--admin-input)] px-4 py-2";
  const selectClass =
    "h-11 w-full rounded-xl border border-[#d7c3a3] bg-white text-black px-3 text-sm outline-none dark:bg-[#181c24] dark:text-white dark:border-white/20";

  return (
    <div className="space-y-6 text-[#2f1618] dark:text-[#fff3dc]">
      <section className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold">Packages / Subscriptions</h2>
            <p className="mt-1 text-sm text-[#7b5a4b] dark:text-[#dbcdb8]/70">
              Manage spiritual tour packages and membership subscriptions.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="admin-btn-primary flex items-center gap-2 rounded-2xl border border-transparent px-4 py-2 text-sm font-medium transition-all"
          >
            <FiPlus size={18} />
            Add Package
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {[
              { key: "all", label: "All" },
              { key: "tour", label: "Tour Packages" },
              { key: "subscription", label: "Subscriptions" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-xl px-4 py-2 text-xs font-semibold ${
                  activeTab === tab.key
                    ? "bg-[var(--admin-primary)] text-white"
                    : "border border-[var(--admin-border)] text-[#6f3945] hover:bg-[var(--admin-surface-soft)]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search package name or destination"
            className="w-full max-w-xs rounded-xl border border-[var(--admin-border)] bg-[var(--admin-input)] px-4 py-2 text-sm"
          />
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-100 p-4 text-red-800 dark:bg-red-900 dark:text-red-200">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-xl bg-green-100 p-4 text-green-800 dark:bg-green-900 dark:text-green-200">
            {success}
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center">Loading packages...</div>
        ) : filteredPackages.length === 0 ? (
          <div className="py-8 text-center">No packages yet. Create a tour or subscription to get started.</div>
        ) : (
          <>
            <div className="admin-table-wrap overflow-x-auto">
              <table className="admin-table w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e6d8c5] text-xs uppercase tracking-[0.18em] text-[#7f5a4f] dark:border-white/10 dark:text-[#e7c98b]">
                    <th className="serial-col px-2 py-3 text-left font-semibold">S.No</th>
                    <th className="px-4 py-3 text-left font-semibold">Package Name</th>
                    <th className="px-4 py-3 text-left font-semibold">Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Destination</th>
                    <th className="px-4 py-3 text-left font-semibold">Duration</th>
                    <th className="px-4 py-3 text-left font-semibold">Price</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPackages.map((item, index) => (
                    <tr
                      key={item._id}
                      className="border-b border-[var(--admin-border)] hover:bg-[var(--admin-surface-soft)]"
                    >
                      <td className="serial-col px-2 py-3">{(page - 1) * pageSize + index + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {item.image ? (
                            <img
                              src={formatImageUrl(item.image)}
                              alt={item.name}
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          ) : null}
                          <div>
                            <p className="font-semibold text-[#D4AF37]">{item.name}</p>
                            <p className="text-xs text-[#7b5a4b]">{categoryLabels[item.category] || item.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                            item.type === "subscription"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                              : "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200"
                          }`}
                        >
                          {typeLabels[item.type] || item.type}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.destination || "-"}</td>
                      <td className="px-4 py-3">{formatDuration(item)}</td>
                      <td className="px-4 py-3">{formatPrice(item)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                            item.isActive
                              ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-package-menu>
                        <div className="relative inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowForm(false);
                              setViewingItem(item);
                            }}
                            className="rounded-lg border border-[#d7bf9b] px-3 py-1.5 text-xs font-semibold text-[#6f3945] hover:bg-[#102C57]/10"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              const nextId = openMenuId === item._id ? "" : item._id;
                              setOpenMenuId(nextId);
                              setMenuAnchorRect(nextId ? event.currentTarget.getBoundingClientRect() : null);
                            }}
                            className="grid h-9 w-9 place-items-center rounded-lg border border-[#d7bf9b] text-[#6f3945] hover:bg-[#102C57]/10 dark:border-white/20 dark:text-[#f7e3c0]"
                          >
                            <FiMoreVertical />
                          </button>
                          {openMenuId === item._id && (
                            <TableMenuPopover
                              open
                              anchorRect={menuAnchorRect}
                              preferUp={index >= pagedPackages.length - 3}
                              onClose={() => setOpenMenuId("")}
                              className="w-44 overflow-hidden rounded-xl border border-[#d9c3a2] bg-white text-sm shadow-lg dark:border-white/10 dark:bg-[#1b1f27]"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenMenuId("");
                                  setShowForm(false);
                                  setViewingItem(item);
                                }}
                                className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-[#102C57]/10"
                              >
                                <FiEye className="text-[#6f3945]" /> View
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenMenuId("");
                                  setViewingItem(null);
                                  openEdit(item);
                                }}
                                className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-[#102C57]/10"
                              >
                                <FiEdit2 className="text-[#6f3945]" /> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenMenuId("");
                                  handleToggleStatus(item);
                                }}
                                className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-[#102C57]/10"
                              >
                                <span className="text-[#6f3945]">
                                  {item.isActive ? "Mark Inactive" : "Mark Active"}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenMenuId("");
                                  handleDelete(item);
                                }}
                                disabled={deletingId === item._id}
                                className="flex w-full items-center gap-2 px-4 py-2 text-left text-red-700 hover:bg-red-50 dark:text-red-200 dark:hover:bg-red-500/10"
                              >
                                <FiTrash2 className="text-red-600" /> Delete
                              </button>
                            </TableMenuPopover>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={filteredPackages.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={[10]}
            />
          </>
        )}
      </section>

      {showForm && (
        <section className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-6 shadow-[var(--admin-shadow)]">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-bold">{editingId ? "Edit Package" : "Create Package / Subscription"}</h3>
            <button onClick={closeForm} className="p-1" title="Close form">
              <FiX size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block font-medium">
                  Package Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g., Karnataka Divine Temple Tour"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block font-medium">Type</label>
                <select name="type" value={form.type} onChange={handleChange} className={selectClass}>
                  <option value="tour">Tour Package</option>
                  <option value="subscription">Subscription / Membership</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block font-medium">Category</label>
                <select name="category" value={form.category} onChange={handleChange} className={selectClass}>
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block font-medium">Destination</label>
                <input
                  type="text"
                  name="destination"
                  value={form.destination}
                  onChange={handleChange}
                  placeholder="e.g., Karnataka, Kerala, Shirdi"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block font-medium">Duration Days</label>
                <input
                  type="number"
                  min="0"
                  name="durationDays"
                  value={form.durationDays}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block font-medium">Duration Nights</label>
                <input
                  type="number"
                  min="0"
                  name="durationNights"
                  value={form.durationNights}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block font-medium">Price (₹)</label>
                <input
                  type="number"
                  min="0"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="60000"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block font-medium">Price Unit</label>
                <select name="priceUnit" value={form.priceUnit} onChange={handleChange} className={selectClass}>
                  {Object.entries(priceUnitLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              {form.type === "subscription" && (
                <>
                  <div>
                    <label className="mb-2 block font-medium">Billing Cycle</label>
                    <select name="billingCycle" value={form.billingCycle} onChange={handleChange} className={selectClass}>
                      <option value="yearly">Yearly</option>
                      <option value="monthly">Monthly</option>
                      <option value="one_time">One Time</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block font-medium">Tours Count</label>
                    <input
                      type="number"
                      min="0"
                      name="toursCount"
                      value={form.toursCount}
                      onChange={handleChange}
                      placeholder="10"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-medium">Validity (Years)</label>
                    <input
                      type="number"
                      min="0"
                      name="validityYears"
                      value={form.validityYears}
                      onChange={handleChange}
                      placeholder="5"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-medium">Zones</label>
                    <input
                      type="number"
                      min="0"
                      name="zonesCount"
                      value={form.zonesCount}
                      onChange={handleChange}
                      placeholder="4"
                      className={inputClass}
                    />
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <label className="mb-2 block font-medium">Travel Dates</label>
                <input
                  type="text"
                  name="travelDates"
                  value={form.travelDates}
                  onChange={handleChange}
                  placeholder="OCT: 10-11, 18-19 | NOV: 07-08"
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block font-medium">Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Package overview for devotees"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block font-medium">Highlights (comma separated)</label>
                <input
                  type="text"
                  name="highlights"
                  value={form.highlights}
                  onChange={handleChange}
                  placeholder="Pay less, Utilize more, Travel more"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block font-medium">Inclusions (comma separated)</label>
                <input
                  type="text"
                  name="inclusions"
                  value={form.inclusions}
                  onChange={handleChange}
                  placeholder="Transportation, Accommodation, Food"
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block font-medium">Cover Image</label>
                <input type="file" accept="image/*" onChange={handleImageChange} className={inputClass} />
                {imagePreview ? (
                  <img src={imagePreview} alt="Package preview" className="mt-3 h-24 rounded-xl object-cover" />
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-[var(--admin-border)] p-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="font-semibold">Day-wise Itinerary</h4>
                <button
                  type="button"
                  onClick={addItineraryDay}
                  className="rounded-xl border border-[var(--admin-border)] px-3 py-1 text-xs font-semibold"
                >
                  Add Day
                </button>
              </div>
              <div className="space-y-3">
                {form.itinerary.map((row, index) => (
                  <div key={`${row.day}-${index}`} className="grid gap-3 md:grid-cols-[80px_1fr_1fr_auto]">
                    <input
                      type="number"
                      min="1"
                      value={row.day}
                      onChange={(event) => updateItinerary(index, "day", event.target.value)}
                      className={inputClass}
                      placeholder="Day"
                    />
                    <input
                      type="text"
                      value={row.title}
                      onChange={(event) => updateItinerary(index, "title", event.target.value)}
                      className={inputClass}
                      placeholder="Day title, e.g., Bangalore - Srirangapatna"
                    />
                    <input
                      type="text"
                      value={row.description}
                      onChange={(event) => updateItinerary(index, "description", event.target.value)}
                      className={inputClass}
                      placeholder="Temples and places covered"
                    />
                    <button
                      type="button"
                      onClick={() => removeItineraryDay(index)}
                      className="rounded-xl border border-red-200 px-3 text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 font-medium">
                <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                Active
              </label>
              <label className="flex items-center gap-2 font-medium">
                <input type="checkbox" name="isFeatured" checked={form.isFeatured} onChange={handleChange} />
                Featured
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="admin-btn-primary flex-1 rounded-2xl border border-transparent px-4 py-2 font-medium disabled:opacity-50"
              >
                {submitting ? "Saving..." : editingId ? "Update Package" : "Create Package"}
              </button>
              <button
                type="button"
                onClick={closeForm}
                disabled={submitting}
                className="flex-1 rounded-2xl border border-[var(--admin-border)] px-4 py-2 font-medium hover:bg-[var(--admin-surface-soft)]"
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {viewingItem && (
        <PackageViewPreview item={viewingItem} onClose={() => setViewingItem(null)} />
      )}
    </div>
  );
}
